/* eslint-disable */
import get from 'lodash/get';
import keyBy from 'lodash/keyBy';
import React, { Component } from 'react';
import PropTypes from 'prop-types';
import classnames from 'classnames';

import { Sortable, SortableItem, SortableHandle } from '~/native-ui/sortable/Sortable';
import { arrayMove } from 'react-sortable-hoc';

import { Box, HStack, Center, ScrollView } from '~/native-ui/native-base';
import { Pressable } from '~/native-ui/Pressable';
import { FormControl } from '~/native-ui/FormControl';
import { Text, HighLightText } from '~/native-ui/Typography';
import { IconButton, Icon } from '~/native-ui/Icon';
import { Spinner } from '~/native-ui/Spinner';
import { Popover } from '~/native-ui/Popover';
import { FlatList } from '~/native-ui/List';
import { SpinnerInput as InjectInput } from '~/native-ui/Spinner';
import { LinkToSpan } from '~/native-ui/Link';
import { renderHTML, extractNativeProps } from '~/native-ui/utils';
import { DropdownOption } from './DropdownOption';

const MaybeSortableListItem = ({ orderable, index, ...props }) => {
    const key = `item-${index}`;
    return !orderable ? (
        props.children
    ) : (
        <SortableItem key={key} index={index}>
            <Box>{props.children}</Box>
        </SortableItem>
    );
};

const renderHtmlAsReact = (text, searchTerm) =>
    typeof text !== 'string'
        ? text
        : renderHTML(text, {
              a: LinkToSpan,
              Text: (props) => (
                  <Text>
                      <HighLightText {...props} searchTerm={searchTerm} />
                  </Text>
              ),
          });

const CenteredSpinner = () => (
    <Center height="34px" width="34px">
        <Spinner size="xs" height="34px" />
    </Center>
);

const IconWrapper = (props) => {
    return <Center {...props} />;
};

const listItemFactory = (
    Input,
    parentProps,
    inputProps,
    { handleBlur, handleFocus, handleClick, renderIcons, renderTemplateValue }
) => {
    const { theme, template, labelKey, required, disabled, isReadOnly } = parentProps;
    return ({ value: selected, index }) => {
        const icons = renderIcons(selected, index);

        const textProps = {
            overflow: 'hidden',
            whiteSpace: 'nowrap',
            textOverflow: 'ellipsis',
        };

        const renderValues =
            template && selected ? (
                renderTemplateValue(selected, { disabled: disabled })
            ) : selected ? (
                <Text {...textProps}>{selected[labelKey]}</Text>
            ) : parentProps.placeholder ? (
                <Text {...textProps} color="muted.400">
                    {parentProps.placeholder}
                </Text>
            ) : null;

        return (
            <Pressable
                flexDirection="row"
                alignItems="center"
                onPress={disabled ? null : handleClick}
                onBlur={disabled ? null : handleBlur}
                backgroundColor={disabled ? '#f5f5f5' : undefined}
                cursor={disabled ? 'not-allowed' : undefined}
            >
                <Box paddingTop="2" paddingBottom="2" flex="1" {...textProps}>
                    {renderValues}
                </Box>
                {disabled || isReadOnly || (
                    <IconWrapper
                        ml="auto"
                        position="sticky"
                        right="0"
                        flexGrow={0}
                        flexShrink={0}
                        alignSelf="flex-start"
                    >
                        {icons}
                    </IconWrapper>
                )}
            </Pressable>
        );
    };
};

const factory = (Input) => {
    class Dropdown extends Component {
        static propTypes = {
            orderable: PropTypes.bool,
            multiSelect: PropTypes.bool,
            allowBlank: PropTypes.bool,
            allowFiltering: PropTypes.bool,
            allowClearing: PropTypes.bool,
            minQueryLength: PropTypes.number,
            auto: PropTypes.bool,
            className: PropTypes.string,
            disabled: PropTypes.bool,
            error: PropTypes.string,
            label: PropTypes.string,
            labelKey: PropTypes.string,
            name: PropTypes.string,
            onBlur: PropTypes.func,
            onChange: PropTypes.func,
            onClick: PropTypes.func,
            onFocus: PropTypes.func,
            onQueryChange: PropTypes.func,
            required: PropTypes.bool,
            source: PropTypes.arrayOf(PropTypes.oneOfType([PropTypes.string, PropTypes.object])).isRequired,
            template: PropTypes.func,
            theme: PropTypes.shape({
                active: PropTypes.string,
                disabled: PropTypes.string,
                dropdown: PropTypes.string,
                error: PropTypes.string,
                errored: PropTypes.string,
                field: PropTypes.string,
                label: PropTypes.string,
                required: PropTypes.string,
                selected: PropTypes.string,
                templateValue: PropTypes.string,
                up: PropTypes.string,
                value: PropTypes.string,
                values: PropTypes.string,
            }),
            value: PropTypes.oneOfType([PropTypes.string, PropTypes.number, PropTypes.array]),
            valueKey: PropTypes.string,
        };

        static defaultProps = {
            auto: true,
            className: '',
            allowBlank: true,
            disabled: false,
            labelKey: 'label',
            required: false,
            valueKey: 'value',
            minCharacters: 0,
            multiSelect: false,
            orderable: false,
        };

        state = {
            active: false,
            up: false,
            minWidth: 0,
            query: '',
            source: [],
        };

        controlledFilter = !!this.props.allowFiltering && !!this.props.onQueryChange;

        dropdownElem = React.createRef(null);
        filterInputElem = React.createRef(null);

        get source() {
            return this.controlledFilter ? this.props.source : this.state.source;
        }

        get query() {
            return this.controlledFilter ? this.props.query : this.state.query;
        }

        componentDidMount() {
            if (!this.controlledFilter) this.onQueryChange('');
        }

        componentDidCatch(error, errorInfo) {
            // You can also log the error to an error reporting service
            console.log(error, errorInfo);
        }

        getSelectedItem = () => {
            for (const item of this.props.source) {
                if (item[this.props.valueKey] === this.props.value) return item;
            }

            return !this.props.allowBlank ? this.props.source[0] : undefined;
        };

        getSelectedItems = () => {
            let valueKey = this.props.valueKey;

            if (!this.props.multiSelect) {
                return [this.getSelectedItem()];
            }

            const itemsByKey = keyBy(this.props.source, valueKey);
            let items = this.props.value.map((value) => itemsByKey[value]).filter(Boolean);
            if (!items.length) {
                items = [undefined];
            }

            return items;
        };

        handleSelect = (val, event) => {
            if (this.props.onBlur) this.props.onBlur(event);
            if (this.isDisabled() || !this.props.onChange) {
                return;
            }

            if (this.props.name) event.target.name = this.props.name;
            if (this.props.multiSelect) {
                val = [...this.props.value, val].filter((v) => typeof v !== 'undefined');
            }

            this.props.onChange(val, event);

            this.close('element selected');
        };

        handleClick = () => {
            if (this.props.disabled) return;
            const openFunc = (event) => {
                this.open(event);
            };
            if (!this.props.onClick) return openFunc;
            return (event) => {
                openFunc(event);
                event.preventDefault();
                this.props.onClick(event);
            };
        };

        close = (reason) => {
            if (this.state.active) {
                //console.log('dropdown closing because of ' + reason)

                this.setState({ active: false });
            }
        };

        open = (event) => {
            if (this.state.active) return;
            const client = event.target.getBoundingClientRect();
            const screenHeight = window.innerHeight || document.documentElement.offsetHeight;
            const up = this.props.auto ? client.top > screenHeight / 2 + client.height : false;
            this.setState({ active: true, up });
        };

        handleFocus = (event) => {
            event.preventDefault();
            if (!this.isDisabled()) this.open(event);
            this.props.onFocus?.(event);
        };

        handleBlur = (event) => {
            event.preventDefault();
            if (this.state.active) this.close('blur');
            this.props.onBlur?.(event);
        };

        renderTemplateValue(selected, props) {
            return <Pressable onPress={this.handleClick()}>{this.props.template(selected, props)}</Pressable>;
        }

        renderValue = (item, idx) => {
            const { labelKey, theme, valueKey, selectedOptionClassName, optionClassName } = this.props;
            const className = classnames(optionClassName, {
                [theme.groupHeader]: item.isGroupHeader,
                [theme.groupOption]: item.isGroupedOption,
                [theme.selected]: item[valueKey] === this.props.value,
                [theme.disabled]: item.disabled || this.isDisabled(),
                [selectedOptionClassName]: selectedOptionClassName && item[valueKey] === this.props.value,
            });

            return (
                <li
                    key={idx}
                    className={className}
                    onMouseDown={
                        !item.disabled && !item.isMetaItem ? this.handleSelect.bind(this, item[valueKey]) : null
                    }
                >
                    {this.props.template ? this.props.template(item) : item[labelKey]}
                </li>
            );
        };

        onQueryChange = (query) => {
            if (this.controlledFilter) {
                this.props.onQueryChange(query);
                return;
            }

            let { labelKey, source } = this.props,
                regex = new RegExp(query, 'ig');

            this.setState({
                query,
                source: source.filter((item) => regex.test(item[labelKey])),
            });
        };

        setSearchFilterRef = (input) => {
            this.filterInputElem = input;
        };

        renderFilterInput = () => {
            const { loading } = this.props;

            return (
                <>
                    <Input
                        loading={loading}
                        value={this.query}
                        ref={this.setSearchFilterRef}
                        px="3"
                        placeholder={get(this.props.query, 'length', 0) ? '' : this.props.filterHint}
                        onChange={this.onQueryChange}
                    />
                </>
            );
        };

        unselectItem = ({ value }) => {
            if (this.props.disabled) return;
            if (this.props.multiSelect) {
                value = this.props.value.filter((i) => i !== value);
            } else {
                value = null;
            }

            this.props.onChange(value);
        };

        isValueEmpty() {
            let { value, multiSelect } = this.props;

            if (multiSelect) {
                return value.filter((v) => typeof v !== 'undefined').length < 1;
            }

            return value === null || value === '';
        }

        renderIcons(item, i) {
            let icons = [];

            if (this.props.loading && i === 0) {
                icons.push(<CenteredSpinner key="spinner" />);
                return icons;
            }

            if (this.props.disabled) {
                if (!icons.length) {
                    icons.push(
                        <IconButton
                            colorScheme="muted"
                            pointerEvents="none"
                            p="5px"
                            opacity="0"
                            rounded="0"
                            roundedTop="xs"
                            type="remove"
                            key="clear"
                        />
                    );
                }
                return <HStack>{icons}</HStack>;
            }

            if (this.isValueEmpty() || !this.props.allowBlank) {
                icons.push(
                    <IconButton
                        colorScheme="muted"
                        p="5px"
                        rounded="0"
                        roundedTop="xs"
                        type="keyboard-arrow-down"
                        key="open"
                        onClick={this.handleClick()}
                    />
                );
            } else if (this.props.allowBlank) {
                icons.push(
                    <IconButton
                        colorScheme="muted"
                        p="5px"
                        rounded="0"
                        roundedTop="xs"
                        type="remove"
                        key="clear"
                        onClick={() => this.unselectItem(item)}
                    />
                );
            }

            if (this.props.orderable && this.getSelectedItems().length > 1) {
                icons.push(
                    <SortableHandle>
                        <Icon
                            colorScheme="muted"
                            p="5px"
                            rounded="0"
                            roundedTop="xs"
                            type="drag-indicator"
                            key="drag"
                            className="dragHandle"
                        />
                    </SortableHandle>
                );
            }

            return <HStack>{icons}</HStack>;
        }

        renderItems() {
            let { source, query, props } = this,
                { valueKey, labelKey, multiSelect, minQueryLength, allowFiltering } = props;

            if (allowFiltering && minQueryLength) {
                if (query.length < minQueryLength) {
                    return [
                        {
                            [labelKey]: `Please enter at least ${minQueryLength - query.length} more characters.`,
                            [valueKey]: 'system_minimum_chars',
                            disabled: true,
                        },
                    ];
                }
            }

            if (multiSelect) {
                source = source.filter((item) => {
                    return this.props.value.indexOf(item[valueKey]) < 0;
                });
            }

            return (
                <FlatList
                    ref={this.listNode}
                    data={source}
                    initialNumToRender={this.props.initialNumToRender}
                    disableVirtualization={this.props.disableVirtualization}
                    renderItem={({ item, index }) => {
                        return (
                            <DropdownOption
                                disabled={item.disabled}
                                borderTopWidth={index === 0 ? 0 : 1}
                                onClick={(e) => this.handleSelect(item[valueKey], e)}
                            >
                                {renderHtmlAsReact(item[labelKey], query)}
                            </DropdownOption>
                        );
                    }}
                    keyExtractor={(item, index) => {
                        return item?.[valueKey] || index;
                    }}
                />
            );
        }

        getListItemElement(inputProps) {
            return listItemFactory(Input, this.props, inputProps, {
                handleBlur: this.handleBlur,
                handleFocus: this.handleFocus,
                handleClick: this.handleClick(),
                renderIcons: this.renderIcons.bind(this),
                renderTemplateValue: this.renderTemplateValue.bind(this),
            });
        }

        renderSelectedItem = (ListItem, orderable, selected, i) => {
            return (
                <MaybeSortableListItem key={`item-${i}`} index={i} orderable={orderable}>
                    <ListItem value={selected} index={i} />
                </MaybeSortableListItem>
            );
        };

        renderSelectedItems = (ListItem) => {
            let selectedItems = this.getSelectedItems();
            //console.log('got selected items', selectedItems);

            const orderable = this.props.orderable && selectedItems.length > 1;
            return selectedItems
                .filter((x) => x)
                .map((selected, i) => this.renderSelectedItem(ListItem, orderable, selected, i));
        };

        handleReorderItems = (oldIndex, newIndex) => {
            let valueKey = this.props.valueKey;
            const values = arrayMove(this.getSelectedItems(), oldIndex, newIndex).map((item) => get(item, valueKey));
            this.props.onChange(values);
        };

        selectedItemsListNode = React.createRef(null);
        setSelectedItemsListNode = (node) => {
            this.selectedItemsListNode = node;
        };

        getDropdownDragContainer = () => {
            return this.selectedItemsListNode;
        };

        setWidth = (event) => {
            const { width } = event.nativeEvent.layout;
            this.setState({ maxWidth: width });
        };

        focusStyle = { borderBottomWidth: 2, borderBottomColor: 'primary.500' };

        renderSelectedItemsContainer(triggerProps, inputProps) {
            let selectedItems = this.getSelectedItems();
            let { valueKey, orderable, multiSelect } = this.props;

            orderable = orderable && selectedItems.length > 1;
            const listItem = this.getListItemElement(inputProps);

            const list = (
                <Box {...triggerProps} borderBottomWidth={1} borderBottomColor="gray.300" _focus={this.focusStyle}>
                    <FlatList
                        onLayout={this.setWidth}
                        ref={this.dropdownElem}
                        data={selectedItems}
                        renderItem={({ item, index }) => this.renderSelectedItem(listItem, orderable, item, index)}
                        keyExtractor={(item, index) => {
                            return item?.[valueKey] || index;
                        }}
                    />
                </Box>
            );

            return !(orderable && selectedItems.length > 1) ? (
                list
            ) : (
                <Sortable
                    useDragHandle
                    getContainer={this.getDropdownDragContainer}
                    onSortEnd={this.handleReorderItems.bind(this)}
                    axis="y"
                >
                    <Box ref={this.setSelectedItemsListNode}>{list}</Box>
                </Sortable>
            );
        }

        isDisabled = () => {
            return this.props.loading || this.props.disabled;
        };

        getToggleIcon = () => {
            if (this.props.loading) {
                return <CenteredSpinner />;
            }

            const selectedItems = this.getSelectedItems();
            return <React.Fragment>{this.renderIcons(selectedItems[0], selectedItems.length)}</React.Fragment>;
        };

        renderInput = (props) => {
            const {
                allowBlank,
                auto,
                labelKey,
                required,
                onChange,
                onFocus,
                onBlur, // eslint-disable-line no-unused-vars
                source,
                template,
                theme,
                minCharacters,
                multiSelect,
                minQueryLength,
                loading,
                filterHint,
                allowFiltering,
                onQueryChange,
                query,
                small,
                valueKey,
                templateValueClassName,
                selectedOptionClassName,
                optionClassName,
                label,
                tooltip,
                error,
                disabled,
                ...others
            } = this.props;

            return this.renderSelectedItemsContainer(props, { disabled, ...others });
            // return <Input { ...props} onFocus={ props.onPress } value={query} onChange={onQueryChange} iconRight={ this.getToggleIcon() } />
        };

        errorStyle = { color: 'danger.500' };
        render() {
            const [nbProps, dropdownProps] = extractNativeProps(this.props);
            const {
                allowBlank,
                auto,
                labelKey,
                required,
                onChange,
                onFocus,
                onBlur, // eslint-disable-line no-unused-vars
                source,
                template,
                theme,
                minCharacters,
                multiSelect,
                minQueryLength,
                loading,
                filterHint,
                allowFiltering,
                onQueryChange,
                query,
                small,
                valueKey,
                templateValueClassName,
                selectedOptionClassName,
                optionClassName,
                label,
                tooltip,
                disabled,
                error,
                isReadOnly,
                isinvalid,
                ...others
            } = dropdownProps;

            return (
                <FormControl
                    mt={!isReadOnly ? 'auto' : undefined}
                    {...nbProps}
                    isRequired={required}
                    isDisabled={disabled}
                    isInvalid={isinvalid}
                >
                    {!error && label && <FormControl.Label key="label" label={label} tooltip={tooltip} />}
                    {error && <FormControl.Label key="label" text={this.errorStyle} label={error} tooltip={tooltip} />}

                    <Popover
                        key="popover"
                        trapFocus
                        shouldFlip
                        isKeyboardDismissable
                        shouldOverlapWithTrigger
                        placement="bottom left"
                        isOpen={!disabled && this.state.active}
                        onOpen={this.handleClick()}
                        onClose={this.close}
                        trigger={this.renderInput}
                        initialFocusRef={this.filterInputElem}
                    >
                        <Popover.Content minWidth={this.state.maxWidth}>
                            <Popover.Body p={0}>
                                {this.props.allowFiltering && this.renderFilterInput()}
                                <ScrollView maxHeight="50vh">{this.renderItems()}</ScrollView>
                            </Popover.Body>
                        </Popover.Content>
                    </Popover>
                </FormControl>
            );
        }
    }

    return React.forwardRef((props, ref) => {
        return <Dropdown {...props} forwardRef={ref} />;
    });
};

const Dropdown = factory(InjectInput);
export default Dropdown;
export { factory as dropdownFactory };
export { Dropdown };
